/**
 * "Pay this now" on the public invoice page: the bank details, and the same
 * SEPA code the printed invoice carries, drawn on screen.
 *
 * The on-screen code is not a duplicate of the printed one - it is the case the
 * printed one cannot serve. A client reading the invoice on a laptop cannot
 * scan their own screen with the phone that holds their bank app... which is
 * exactly what this is for: the laptop shows the code, the phone reads it.
 *
 * The payload comes from the server (`payEpc`), never assembled here. One
 * builder, one set of rules about what a SEPA transfer may carry, and no chance
 * of the screen asking for a sum the paper did not.
 */
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Card, CardContent } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { fmtMoney } from "../../lib/format";
import type { PublicDoc } from "../../lib/publicDoc";

export function DocPayment({ doc }: { doc: PublicDoc }) {
  const t = useT();
  const { locale } = useI18n();
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (!doc.payEpc) {
      setQr(null);
      return;
    }
    // Always dark on white, whatever the page theme is: a code inverted for a
    // dark background is a code half the scanners refuse.
    QRCode.toDataURL(doc.payEpc, { margin: 1, width: 320, errorCorrectionLevel: "M" })
      .then(setQr)
      .catch(() => setQr(null));
  }, [doc.payEpc]);

  const rows: [string, string][] = [];
  if (doc.seller.name) rows.push([t("publicDoc.pay.payee"), doc.seller.name]);
  if (doc.seller.iban) rows.push([t("publicDoc.pay.iban"), doc.seller.iban]);
  if (doc.seller.bankName) rows.push([t("publicDoc.pay.bank"), doc.seller.bankName]);
  rows.push([t("publicDoc.pay.amount"), fmtMoney(doc.dueCents, doc.currency, locale)]);
  rows.push([t("publicDoc.pay.reference"), `${t("publicDoc.kind.invoice")} ${doc.number}`]);

  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-6 pt-6">
        <div className="min-w-[240px] flex-1">
          <h2 className="text-base font-semibold text-foreground">{t("publicDoc.pay.title")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {qr ? t("publicDoc.pay.hint") : t("publicDoc.pay.hintNoCode")}
          </p>
          <dl className="mt-4 space-y-1.5 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="flex flex-wrap gap-x-3">
                <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
                <dd className="min-w-0 break-words font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        {qr ? (
          <figure className="m-0 shrink-0 text-center">
            <img src={qr} alt={t("publicDoc.pay.title")} width={160} height={160} className="rounded-lg bg-white p-2" />
            <figcaption className="mt-2 text-xs text-muted-foreground">{t("publicDoc.pay.scan")}</figcaption>
          </figure>
        ) : null}
      </CardContent>
    </Card>
  );
}
